import type { GridCardItem } from 'nebula-ui-kit';
import { formatVaultLocalDateTime } from '@nebula/shared-vault/data-access';
import type { DashboardStatsResponse } from '../../../../domain/src/lib/dashboard/dashboard.model';

function humanizeKey(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function toSentenceCase(text: string): string {
  const t = text.trim();
  if (!t) return t;
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function formatDateTime(value: string): string {
  return formatVaultLocalDateTime(value);
}

function describeAction(actionRaw: string, entityRaw: string): string {
  const action = actionRaw.trim().toLowerCase();
  const entity = entityRaw.trim().toLowerCase();

  const entityLabelMap: Record<string, string> = {
    legal_hold: 'una custodia legal',
    retention_policy: 'una política de retención',
    retention: 'una retención',
    ingest: 'una ingesta de documentos',
    document: 'un documento',
    user: 'un usuario',
    notification: 'una notificación',
    audit: 'un registro de auditoría',
  };

  const entityLabel = entityLabelMap[entity] ?? `el recurso ${entity.replace(/_/g, ' ')}`;

  if (action === 'create') return `Se creó ${entityLabel}`;
  if (action === 'update') return `Se actualizó ${entityLabel}`;
  if (action === 'delete') return `Se eliminó ${entityLabel}`;
  if (action === 'read' || action === 'view') return `Se consultó ${entityLabel}`;
  if (action === 'login') return 'Un usuario inició sesión';
  if (action === 'logout') return 'Un usuario cerró sesión';
  if (!actionRaw.trim()) return 'Se registró una actividad del sistema';
  return `Se ejecutó la acción "${actionRaw}" sobre ${entityLabel}`;
}

function getNested(obj: unknown, path: string[]): unknown {
  let cur: unknown = obj;
  for (const key of path) {
    if (!cur || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[key];
  }
  return cur;
}

/** Prueba varias rutas (variantes del API) y devuelve el primer valor escalar usable. */
function pickMetric(stats: DashboardStatsResponse, paths: string[][]): unknown {
  for (const path of paths) {
    const v = getNested(stats, path);
    if (v !== null && v !== undefined) return v;
  }
  return undefined;
}

function scalarToDisplay(v: unknown): string | null {
  if (v === null || v === undefined) return null;
  if (typeof v === 'number' || typeof v === 'bigint') return String(v);
  if (typeof v === 'boolean') return v ? 'Sí' : 'No';
  if (typeof v === 'string') return v.length > 48 ? `${v.slice(0, 45)}…` : v;
  return null;
}

/**
 * Solo KPIs explícitos que resumen operación del sistema (sin aplanar todo el JSON).
 */
export function buildDashboardGridItems(stats: DashboardStatsResponse | null): GridCardItem[] {
  const out: GridCardItem[] = [];
  if (!stats) return out;

  const curated: Array<{
    title: string;
    icon: GridCardItem['icon'];
    paths: string[][];
    statusColor: GridCardItem['statusColor'];
  }> = [
    {
      title: 'Ingestas totales',
      icon: 'Upload',
      paths: [
        ['document_stats', 'ingest_stats', 'total_ingests'],
        ['document_stats', 'total_ingests'],
      ],
      statusColor: 'info',
    },
    {
      title: 'Ingestas hoy',
      icon: 'Activity',
      paths: [['document_stats', 'ingest_stats', 'ingests_today']],
      statusColor: 'info',
    },
    {
      title: 'Pendientes de procesamiento',
      icon: 'Clock',
      paths: [['document_stats', 'ingest_stats', 'pending_processing']],
      statusColor: 'info',
    },
  ];

  for (const spec of curated) {
    const raw = pickMetric(stats, spec.paths);
    const value = scalarToDisplay(raw);
    if (value === null) continue;
    out.push({
      type: 'scorecard',
      title: spec.title,
      value,
      icon: spec.icon,
      statusColor: spec.statusColor,
      iconPosition: 'right',
    });
  }

  return out;
}

function normalizeEntityLabel(entityRaw: string): string {
  const e = entityRaw.trim().toLowerCase();
  const map: Record<string, string> = {
    legal_hold: 'Custodia Legal',
    retention_policy: 'Retención Documental',
    retention: 'Retención Documental',
    ingest: 'Carga de Documentos',
    document: 'Documento',
    user: 'Usuario',
    notification: 'Notificaciones',
    audit: 'Auditoría',
    repository: 'Bóveda Digital',
    repository_admin: 'Bóveda Digital',
    cloud: 'Respaldo Externo',
    preservation: 'Preservación Digital',
  };
  return map[e] ?? humanizeKey(e || 'sistema');
}

function normalizeActionLabel(actionRaw: string): string {
  const a = actionRaw.trim().toLowerCase();
  if (a === 'create') return 'Creación';
  if (a === 'update') return 'Actualización';
  if (a === 'delete') return 'Eliminación';
  if (a === 'read' || a === 'view') return 'Consulta';
  if (a === 'login') return 'Inicio de sesión';
  if (a === 'logout') return 'Cierre de sesión';
  return toSentenceCase(actionRaw || 'Actividad');
}

function normalizeMainText(
  message: string,
  title: string,
  action: string,
  entityType: string,
): string {
  const technicalPattern = /^(create|update|delete|read|view)\s+en\s+([a-z0-9_]+)$/i;
  const fromMessage = message.match(technicalPattern);
  if (fromMessage) {
    return `${normalizeActionLabel(fromMessage[1])} en ${normalizeEntityLabel(fromMessage[2])}`;
  }
  const fromTitle = title.match(technicalPattern);
  if (fromTitle) {
    return `${normalizeActionLabel(fromTitle[1])} en ${normalizeEntityLabel(fromTitle[2])}`;
  }
  if (message) return toSentenceCase(message);
  if (title) return toSentenceCase(title);
  return `${normalizeActionLabel(action)} en ${normalizeEntityLabel(entityType)}`;
}

export function formatActivityLine(row: Record<string, unknown>): string {
  const message = typeof row['message'] === 'string' ? row['message'].trim() : '';
  const title = typeof row['title'] === 'string' ? row['title'].trim() : '';
  const action = typeof row['action'] === 'string' ? row['action'] : '';
  const entityType = typeof row['entity_type'] === 'string' ? row['entity_type'] : '';
  const statusRaw = typeof row['status'] === 'string' ? row['status'].trim() : '';
  const status = statusRaw.toLowerCase();
  const dateRaw =
    (typeof row['date'] === 'string' && row['date']) ||
    (typeof row['created_at'] === 'string' && row['created_at']) ||
    '';

  const actor =
    (typeof row['user_name'] === 'string' && row['user_name'].trim()) ||
    (typeof row['user_email'] === 'string' && row['user_email'].trim()) ||
    (typeof row['user_id'] === 'string' && row['user_id'].trim()) ||
    '';

  const mainText = normalizeMainText(message, title, action, entityType);
  const statusLabel =
    !status || status === 'success'
      ? ''
      : status === 'error' || status === 'failed'
        ? 'Resultado: con error'
        : `Resultado: ${statusRaw}`;
  const date = dateRaw ? formatDateTime(dateRaw) : '';
  const actorLabel =
    actor && actor.length < 24 && !actor.includes('@')
      ? `Responsable: ${actor}`
      : actor && actor.includes('@')
        ? `Responsable: ${actor}`
        : '';

  const parts = [mainText, actorLabel, statusLabel, date && `Fecha: ${date}`]
    .filter(Boolean);

  if (parts.length > 0) return parts.join(' · ');

  // Fallback mínimo si el backend manda otro shape.
  const flat = Object.entries(row)
    .filter(([, v]) => v !== null && v !== undefined)
    .slice(0, 4)
    .map(([k, v]) => `${humanizeKey(k)}: ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`);
  return flat.join(' · ') || 'Evento sin detalle';
}
